/**
 * All workspace data is saved here.
 */
import { reactive, computed } from "vue";
import { filter } from "lodash-es";
import { ensureInt } from "@/helpers/utility/Type";
import envOptions from "@/helpers/Env";
import eventHub from "@/helpers/utility/Events";
import * as API from "@/api/API";

import { useUser } from "@/api/user.store";
import { getUserDB } from "@/api/DB";
import { NotFoundError } from "@core/errors/Errors.js";
import { useWorkspaceModelsDB } from "./models.db";
import { useModels } from "./models.store";

import type {
  OrganizationWorkspace,
  PersonalWorkspace,
  Workspace,
  WorkspaceFolder,
  WorkspaceMember
} from "./Workspace";
import type { User } from "./User";

const workspaces = reactive<{ [id: number]: Workspace | null }>({});

/**
 * @returns
 */
export function useWorkspaces(props?: {
  teamSlug?: string;
  organizationSlug?: string;
  folderSlug?: string;
}) {
  const { user } = useUser();

  /**
   * Returns the local db workspace table
   * @returns {Dexie.Table}
   */
  function getWorkspaceDB() {
    const db = getUserDB(user.value?.id);
    return db.workspaces;
  }

  /**
   * All workspaces, keyed by their slug, for quick access:
   */
  const workspacesBySlug = computed(() => {
    const list: { [slug: string]: Workspace } = {};
    for (const workspaceId in workspaces) {
      const workspace = workspaces[workspaceId];
      if (workspace) list[workspace.slug] = workspace;
    }
    return list;
  });

  const slugWorkspace = computed(() => {
    return Object.values(workspaces).find(
      (w) =>
        w?.slug === props?.teamSlug &&
        w?.organizationSlug === props?.organizationSlug
    );
  });

  const userWorkspace = computed(() => {
    if (user.value?.workspace?.id == null) return null;
    return workspaces[user.value?.workspace?.id] ?? null;
  });

  const currentWorkspace = computed(() => {
    if (props?.organizationSlug != null) return slugWorkspace.value;
    else return userWorkspace.value;
  });

  const currentWorkspaceFolders = computed(() => {
    if (currentWorkspace.value == null) return [];

    const folders = (currentWorkspace.value?.folders ?? []).slice();

    folders.sort((a, b) => {
      if (a.order < b.order) return -1;
      else if (a.order == b.order) return 0;
      else return 1;
    });

    return folders;
  });

  const currentFolder = computed(() => {
    const folders = currentWorkspace.value?.folders ?? [];
    return folders.find((f) => f.slug == props?.folderSlug);
  });

  const teams = computed(() => {
    return Object.values(workspaces).filter(
      (w): w is OrganizationWorkspace => w?.organizationSlug != null
    );
  });

  const currentTeams = computed(() => {
    return teams.value.filter(
      (w) => w?.organizationSlug === props?.organizationSlug
    );
  });

  /**
   * Return ONLY the workspaces the user is a part of.
   */
  const userTeams = computed(() => {
    return teams.value.filter((ws) => {
      if (ws?.expired) return false;
      const membership = ws?.membership;
      return membership != null;
    });
  });

  /**
   * All Workspaces the user has access to:
   * (personal AND organization)
   */
  const userWorkspaces = computed(() => {
    return [userWorkspace.value].concat(userTeams.value);
  });

  const teamsByOrganization = computed(() => {
    const result: { [organizationSlug: string]: OrganizationWorkspace[] } = {};
    for (const team of teams.value) {
      if (team == null) continue;
      if (team.organizationSlug == null) continue;
      if (!result[team.organizationSlug]) result[team.organizationSlug] = [];
      result[team.organizationSlug].push(team);
    }
    return result;
  });

  /**
   * Update the workspace entry in memory.
   */
  const updateWorkspaceEntry = (workspaceId: number, workspace: Workspace | null) => {
    const entry = workspaces[workspaceId];
    if (workspace) {
      if (entry) {
        Object.assign(entry, workspace);
      } else {
        workspaces[workspaceId] = workspace;
      }
    } else if (entry) {
      delete workspaces[workspaceId];
    }
  };

  function updateWorkspaceMemberEntry(
    workspace: { id: number },
    memberId: number,
    member: WorkspaceMember | null = null
  ) {
    const workspaceEntry = workspaces[workspace.id];
    if (workspaceEntry == null) {
      throw new Error(`Workspace ${workspace.id} does not exist.`);
    }
    if (!workspaceEntry.members) workspaceEntry.members = [];
    const members = workspaceEntry.members;
    const memberIndex = members.findIndex((m) => m.id == memberId);

    if (member) {
      // update membership in list

      if (memberIndex > -1) {
        members.splice(memberIndex, 1, member);
      } else {
        members.push(member);
      }
    } else {
      // remove membership from list

      if (memberIndex > -1) {
        members.splice(memberIndex, 1);
      }
    }

    if (memberId === user.value?.id) {
      workspaceEntry.membership = member;
    }
  }

  const loadTeam = async (organizationSlug: string, teamSlug: string) => {
    const team = await API.get<Workspace>(
      `/v1/organizations/${organizationSlug}/teams/${teamSlug}`,
      {
        async synch(makeRequest) {
          const workspaceDB = getWorkspaceDB();

          const workspace = await workspaceDB
            .where({ organizationSlug, slug: teamSlug })
            .first();

          makeRequest()
            .then((workspace) => {
              workspaceDB.put(workspace);
              updateWorkspaceEntry(workspace.id, workspace);
              return workspace;
            })
            .catch((error) => {
              if (error.status != null) {
                console.error(error);
              }
              if (error.status == 404) {
                console.log(
                  `workspace ${organizationSlug}/${teamSlug} not found, delete it`
                );

                if (workspace) {
                  // remove entry
                  workspaces[workspace.id] = null;
                  workspaceDB.delete(workspace.id);
                }
              }
            });

          if (workspace == null) {
            throw new NotFoundError();
          }

          console.log(
            `%cfrom cache: workspace ${organizationSlug}/${teamSlug}`,
            "background-color: yellow; padding: 5px 1rem;"
          );

          return workspace;
        }
      }
    );
    updateWorkspaceEntry(team.id, team);
    return team;
  };

  /**
   * Loads all workspaces the user has access to
   */
  async function loadWorkspaces() {
    const workspaces = await API.get<Workspace[]>("/v1/workspaces", {
      synch: loadWorkspacesCached
    });

    for (const workspace of workspaces) {
      updateWorkspaceEntry(workspace.id, workspace);
    }

    return workspaces;
  }

  /**
   * Load workspaces from cache first.
   */
  async function loadWorkspacesCached(makeRequest: () => Promise<Workspace[]>) {
    const workspaceDB = getWorkspaceDB();
    const cachedWorkspaces = await workspaceDB.toArray();

    const request = makeRequest().then(async (workspaces) => {
      const workspaceIds = workspaces.map((ws) => ws.id);

      // cache the workspaces
      const existingIds = cachedWorkspaces.map((ws) => ws.id);
      for (const workspace of workspaces) {
        if (existingIds.includes(workspace.id)) {
          workspaceDB.update(workspace.id, workspace);
        } else {
          workspaceDB.put(workspace);
        }
      }

      const deletedWorkspaces = cachedWorkspaces.filter(
        (ws) => !workspaceIds.includes(ws.id)
      );
      workspaceDB.bulkDelete(deletedWorkspaces.map((ws) => ws.id));

      const updatedWorkspaces = await workspaceDB.toArray();

      return updatedWorkspaces;
    });

    if (!cachedWorkspaces || cachedWorkspaces.length == 0) {
      return request;
    } else {
      request
        .then((remoteWorkspaces) => {
          for (const workspace of remoteWorkspaces) {
            updateWorkspaceEntry(workspace.id, workspace);
          }
        })
        .catch((error) => {
          if (error.status == null) {
            console.warn("workspaces loading failed (no connection)");
          } else {
            console.warn("workspaces loading failed", error);
          }
        });
    }

    console.log(
      "%cfrom cache: workspaces",
      "background-color: yellow; padding: 5px 1rem;"
    );

    return cachedWorkspaces;
  }

  /**
   * Get a workspace by its ID and save it.
   */
  const loadWorkspace = async (workspaceId: number) => {
    const workspace = await API.get<Workspace>(
      `/v1/workspaces/${workspaceId}`,
      {
        params: {
          workspaceId
        },
        synch: loadWorkspaceCached
      }
    );

    updateWorkspaceEntry(workspace.id, workspace);

    return workspace;
  };

  async function loadWorkspaceCached(
    makeRequest: () => Promise<Workspace>,
    {
      params
    }: API.RequestOptions<Workspace> & { params: { workspaceId: number } }
  ) {
    const workspaceId = params.workspaceId;
    const workspaceDB = getWorkspaceDB();
    const workspace = await workspaceDB.get({ id: workspaceId });

    const request = makeRequest().then((ws) => {
      console.log("put: from api", ws);
      workspaceDB.put(ws);
      return ws;
    });

    if (workspace == null) {
      console.log(workspaceDB);
      console.log("no workspace", workspaceId, "in DB, awaiting API");
      return request;
    }

    // Handle updated data in background:
    request
      .then((remoteWorkspace) => {
        updateWorkspaceEntry(remoteWorkspace.id, remoteWorkspace);
      })
      .catch((error) => {
        if (error.status == null) {
          console.warn("fetch workspace failed (no connection)");
        } else if (error.status == 404) {
          console.log(`workspace ${workspaceId} not found, delete it`);
          // remove entry
          workspaces[workspaceId] = null;
          workspaceDB.delete(workspaceId);
        } else {
          console.warn("fetch workspace failed", error);
        }
      });

    console.log(
      "%cfrom cache: workspace",
      "background-color: yellow; padding: 5px 1rem;",
      workspace
    );

    return workspace;
  }

  /**
   * Join an existing team. Only works in teams the user has managing rights in.
   * This action is for organization admins that want to join one of their teams.
   */
  async function joinTeam(team: {
    id: number;
    organizationSlug: string;
    slug: string;
  }) {
    const organizationSlug = team.organizationSlug;
    const teamSlug = team.slug;

    const newMember = await API.post<WorkspaceMember>(
      `/v1/organizations/${organizationSlug}/teams/${teamSlug}/members`,
      { data: { user: user.value } }
    );

    updateWorkspaceMemberEntry(team, newMember.id, newMember);

    return newMember;
  }

  /**
   * Invite new members to a team. Every member will receive an email invitation.
   * Until they accept their invitation, they will be "pending" - other team members won't
   * be able to see their full names; and they won't be able to access the team's models.
   */
  async function inviteTeamMembers(
    team: { id: number; organizationSlug: string; slug: string },
    entries: {
      user: { email: string };
      validUntil?: string;
      roleCode?: string;
    }[]
  ) {
    const organizationSlug = team.organizationSlug;
    const teamSlug = team.slug;
    const newMembers = await API.post<WorkspaceMember[]>(
      `/v1/organizations/${organizationSlug}/teams/${teamSlug}/members`,
      {
        data: entries
      }
    );

    // add the members to the team entry
    const workspaceEntry = workspaces[team.id];

    if (workspaceEntry) {
      workspaceEntry.members = (workspaceEntry.members ?? []).concat(
        newMembers
      );
    }

    return newMembers;
  }

  /**
   * Accept a pending team invitation. After accepting, the user will be a full member
   * of the team.
   */
  async function acceptTeamInvitation(
    team: { organizationSlug: string; slug: string; id: number },
    user: { id: number }
  ) {
    const organizationSlug = team.organizationSlug;
    const teamSlug = team.slug;
    const userId = user.id;
    const updatedMember = await API.post<WorkspaceMember>(
      `/v1/organizations/${organizationSlug}/teams/${teamSlug}/members/${userId}/accept`
    );

    updateWorkspaceMemberEntry(team, updatedMember.id, updatedMember);

    return updatedMember;
  }

  /**
   * Change the settings of a team member (usually the role)
   */
  async function updateTeamMember(
    team: { organizationSlug: string; slug: string; id: number },
    member: { id: number }
  ) {
    const organizationSlug = team.organizationSlug;
    const teamSlug = team.slug;
    const memberId = member.id;
    const updatedMember = await API.patch<WorkspaceMember>(
      `/v1/organizations/${organizationSlug}/teams/${teamSlug}/members/${memberId}`,
      { data: member }
    );

    updateWorkspaceMemberEntry(team, memberId, updatedMember);

    return updatedMember;
  }

  /**
   * Remove a member from a team.
   * That member loses ANY access to the team, including all models in it.
   */
  async function removeTeamMember(
    team: { organizationSlug: string; slug: string; id: number },
    member: { id: number }
  ) {
    const organizationSlug = team.organizationSlug;
    const teamSlug = team.slug;
    const memberId = member.id;

    await API.destroy(
      `/v1/organizations/${organizationSlug}/teams/${teamSlug}/members/${memberId}`
    );

    updateWorkspaceMemberEntry(team, memberId, null);
  }

  /**
   * Leave the specified team. Works only if user is member of team.
   */
  async function leaveTeam(team: {
    organizationSlug: string;
    slug: string;
    id: number;
    membership: { id: number } | null;
  }) {
    if (team.membership == null) {
      throw new Error("User is not a member of this team.");
    }
    return await removeTeamMember(team, team.membership);
  }

  /**
   *
   */
  function updateWorkspaceEntryFolder(
    workspaceId: number,
    folderId: number,
    folder: WorkspaceFolder | null
  ) {
    // add the updated folder to the workspace
    const workspace = workspaces[workspaceId];
    if (workspace == null) {
      console.error(`Workspace ${workspaceId} does not exist.`);
      return;
    }

    let folders = workspace.folders;
    if (!folders) {
      workspace.folders = [];
      folders = workspace.folders;
    }
    const folderIndex = folders.findIndex((f) => f.id == folderId);
    if (folderIndex === -1 && folder != null) {
      folders.push(folder);
    } else if (folder == null) {
      folders.splice(folderIndex, 1);
    } else {
      Object.assign(folders[folderIndex], folder);
    }
  }

  return {
    workspacesBySlug,
    workspaces,
    currentWorkspace,
    currentFolder,
    currentWorkspaceFolders,
    slugWorkspace,
    teams,
    teamsByOrganization,
    currentTeams,

    userWorkspace,
    userWorkspaces,
    userTeams,

    updateWorkspaceEntry,

    loadWorkspace,
    loadWorkspaces,
    loadTeam,

    acceptTeamInvitation,
    inviteTeamMembers,
    updateTeamMember,
    removeTeamMember,
    leaveTeam,
    joinTeam,

    // WORKSPACE

    async createFolder(folderData: { workspace: number; name: string }) {
      const workspaceId = folderData.workspace;
      const folder = await API.post<WorkspaceFolder>(
        `/v1/workspaces/${workspaceId}/folders`,
        {
          data: folderData
        }
      );

      updateWorkspaceEntryFolder(workspaceId, folder.id, folder);

      if (envOptions.saveLocally) {
        const workspace = workspaces?.[workspaceId];

        if (workspace) {
          const foldersToSave = (workspace.folders ?? []).map((f) => ({
            ...f
          }));
          const workspaceDB = getWorkspaceDB();
          await workspaceDB.update(ensureInt(workspaceId), {
            folders: foldersToSave
          });
        }
      }

      return folder;
    },
    async destroyFolder(folder: { workspace: number; id: number }) {
      const workspaceId = folder.workspace;
      const folderId = folder.id;

      await API.destroy(`/v1/workspaces/${workspaceId}/folders/${folderId}`);

      // remove the folder from the record
      const workspace = workspaces?.[workspaceId];
      if (workspace) {
        updateWorkspaceEntryFolder(workspaceId, folder.id, null);
      }

      // remove all models in the folder
      const modelsStore = useModels();
      const folderModels = filter(modelsStore.models, (model) => model.folder == folderId);
      for (const model of folderModels) {
        delete modelsStore.models[model.id];
      }

      if (envOptions.saveLocally) {
        if (workspace) {
          const foldersToSave = (workspace.folders || []).map((f) => ({
            ...f
          }));
          const workspaceDB = getWorkspaceDB();
          await workspaceDB.update(ensureInt(workspaceId), {
            folders: foldersToSave
          });
        }
      }
    },
    async updateFolder(
      folderData: Partial<WorkspaceFolder> & { id: number; workspace: number }
    ) {
      const folderId = folderData.id;
      const workspaceId = folderData.workspace;
      const updatedFolder = await API.patch<WorkspaceFolder>(
        `/v1/workspaces/${workspaceId}/folders/${folderId}`,
        {
          data: folderData
        }
      );

      updateWorkspaceEntryFolder(workspaceId, folderId, updatedFolder);

      return updatedFolder;
    },

    /**
     * Duplicate a folder of a workspace
     */
    async copyFolder(
      folder: { id: number; workspace: number },
      data: { workspaceId?: number } = {}
    ) {
      const workspaceId = folder.workspace;
      const folderId = folder.id;

      const newFolder = await API.post<WorkspaceFolder>(
        `/v1/workspaces/${workspaceId}/folders/${folderId}/copy`,
        {
          data: {
            ...data,
            workspaceId: data?.workspaceId ?? workspaceId
          }
        }
      );

      updateWorkspaceEntryFolder(newFolder.workspace, newFolder.id, newFolder);

      if (envOptions.saveLocally) {
        // update the local database entry
        const workspace = workspaces?.[workspaceId];
        if (workspace) {
          const foldersToSave = (workspace.folders || []).map((f) => ({
            ...f
          }));
          const workspaceDB = getWorkspaceDB();
          await workspaceDB.update(ensureInt(workspaceId), {
            folders: foldersToSave
          });
        }
      }

      return newFolder;
    },

    /**
     * Move a folder to another workspace
     */
    async moveFolder(
      folder: {
        id: number;
        slug: string;
        workspace: number;
      },
      newWorkspaceId: number
    ) {
      const folderId = folder.id;
      const workspaceId = folder.workspace;

      const newFolder = await API.patch<WorkspaceFolder>(
        `/v1/workspaces/${workspaceId}/folders/${folderId}`,
        {
          data: {
            id: folderId,
            workspace: newWorkspaceId
          }
        }
      );

      updateWorkspaceEntryFolder(newWorkspaceId, folderId, newFolder);

      if (envOptions.saveLocally) {
        const workspaceDB = getWorkspaceDB();

        // update the local database entry
        const newWorkspace = workspaces?.[newWorkspaceId];
        if (newWorkspace) {
          const foldersToSave = (newWorkspace.folders || []).map((f) => ({
            ...f
          }));
          await workspaceDB.update(ensureInt(newWorkspaceId), {
            folders: foldersToSave
          });
        }
        const oldWorkspace = workspaces?.[workspaceId];
        if (oldWorkspace) {
          const foldersToSave = (oldWorkspace.folders || []).map((f) => ({
            ...f
          }));
          await workspaceDB.update(ensureInt(workspaceId), {
            folders: foldersToSave
          });
        }

        // change the workspace id of all models within the folder
        const modelsDB = useWorkspaceModelsDB().getModelsDB();
        await modelsDB.where({ folder: folderId }).modify({
          workspace: newWorkspaceId
        });
      }

      // update all models in the folder,
      // so they are moved to the new workspace.
      useModels().getFolderModels({ id: newWorkspaceId }, folder.slug);

      return newFolder;
    },

    /**
     * Change the order of a folder in a workspace
     */
    async reorderFolder(folder: {
      id: number;
      workspace: number;
      order: number;
    }) {
      const folderId = folder.id;
      const workspaceId = folder.workspace;

      await API.post(`/v1/workspaces/${workspaceId}/folders/${folderId}/move`, {
        data: folder
      });

      return await loadWorkspace(workspaceId);
    },

    /**
     * Get a list of all members of a team.
     */
    async getTeamMembers(team: { id: number }) {
      const teamId = team.id;
      const members = await API.get<WorkspaceMember[]>(
        `/v1/workspaces/${teamId}/members`
      );

      const workspaceEntry = workspaces[teamId];
      if (workspaceEntry) {
        workspaceEntry.members = members;
      }

      return members;
    }
  };
}

export default {
  workspaces
};

function reset() {
  for (const workspaceId in workspaces) {
    delete workspaces[workspaceId];
  }
}

eventHub.on("user-changed", async (user: User | null) => {
  console.log("%creseting workspace store", "color: lightgrey;");
  reset();
  
  if (user) {
    // reload the workspaces the user has access to:
    useWorkspaces().loadWorkspaces();
  }
});

export function isPersonalWorkspace(
  workspace: Workspace
): workspace is PersonalWorkspace {
  return workspace.isPersonal;
}
export function isOrganizationWorkspace(
  workspace: Workspace
): workspace is OrganizationWorkspace {
  return !workspace.isPersonal;
}
